/**
 * SignupStepShell — shared chrome for every wizard step.
 *
 * Renders the iAdvocate header, a 5-segment progress bar that highlights
 * `currentStepIndex`, optional Back/Skip controls, and a slot for the
 * step body. Keeps each step component focused on its form.
 */
import React from 'react';
import { motion } from 'framer-motion';
import { Scale, ChevronLeft } from 'lucide-react';
import { cn } from '../../lib/utils';
import { STEP_ORDER, StepName, NON_SKIPPABLE_STEPS } from './signupApi';

interface SignupStepShellProps {
  stepName: StepName;
  title: string;
  subtitle?: string;
  onBack?: () => void;
  onSkip?: () => void;
  skipPending?: boolean;
  children: React.ReactNode;
}

const SignupStepShell: React.FC<SignupStepShellProps> = ({
  stepName,
  title,
  subtitle,
  onBack,
  onSkip,
  skipPending,
  children,
}) => {
  const currentIndex = STEP_ORDER.indexOf(stepName);
  const canSkip = !NON_SKIPPABLE_STEPS.has(stepName) && !!onSkip;

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface p-4">
      <div className="max-w-3xl w-full">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <div className="w-14 h-14 bg-gradient-to-br from-navy-700 to-navy-900 dark:from-gold-400 dark:to-gold-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Scale className="h-7 w-7 text-white" />
          </div>
          <h1 className="font-serif text-2xl md:text-3xl font-bold text-text-primary mb-2">
            {title}
          </h1>
          {subtitle && (
            <p className="text-text-secondary max-w-xl mx-auto">{subtitle}</p>
          )}
        </motion.div>

        {/* 5-segment progress bar */}
        <div
          className="flex items-center justify-center gap-2 mb-8"
          role="progressbar"
          aria-valuenow={currentIndex + 1}
          aria-valuemin={1}
          aria-valuemax={STEP_ORDER.length}
          aria-label={`Step ${currentIndex + 1} of ${STEP_ORDER.length}`}
        >
          {STEP_ORDER.map((s, i) => (
            <div
              key={s}
              className={cn(
                'w-8 h-1 rounded-full transition-colors',
                i <= currentIndex ? 'bg-legal-green-500' : 'bg-border-primary',
              )}
            />
          ))}
        </div>

        {/* Top row controls — Back on the left, Skip on the right */}
        {(onBack || canSkip) && (
          <div className="flex items-center justify-between mb-4 px-1">
            {onBack ? (
              <button
                onClick={onBack}
                className="inline-flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors"
              >
                <ChevronLeft className="h-4 w-4" />
                Back
              </button>
            ) : (
              <span />
            )}
            {canSkip && (
              <button
                onClick={onSkip}
                disabled={skipPending}
                className="text-sm text-text-tertiary hover:text-text-secondary disabled:opacity-50 transition-colors"
              >
                Skip for now
              </button>
            )}
          </div>
        )}

        {/* Step body */}
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          {children}
        </motion.div>
      </div>
    </div>
  );
};

export default SignupStepShell;
